/// <amd-module name="Wit/WorkItemGitLinks/Components/RefPicker" />
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePageContext } from "VSS/Platform/Layout";
import { IObservableArray, useObservableArray } from "VSS/Core/Observable";
import { Dropdown, IDropdownPivot, IWithPivotsChildProps, WithPivots } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { getGitClient } from "VC/Common/RestClient/Git";
import { GitRepository, GitRef } from "VC/Common/Contracts/Git";
import * as Resources from "Wit/WorkItemGitLinks/Resources";
import { IVssPageContext } from "VSS/Platform/Context";
import { formatBranchName, formatTagName } from "../GitLinkUtils";

const myBranchesPivotId = "my-branches";
const allBranchesPivotId = "all-branches";
const tagsPivotId = "tags";
const branchesFilter = "heads/";
const branchRefPrefix = "refs/heads/";
const tagsFilter = "tags/";

export interface IRefPickerProps {
    repository: GitRepository | undefined;
    onSelect: (ref: GitRef) => void;
    excludeBranches?: boolean;
    excludeTags?: boolean;
}

export function RefPicker({ repository, onSelect, excludeBranches, excludeTags }: IRefPickerProps) {
    const { disableDropdown, selectedPivot, dropdownItems, pivots, onPivotClicked } = useBranchPivotData(
        repository,
        excludeBranches ?? false,
        excludeTags ?? false
    );

    return (
        <WithPivots pivots={pivots} selectedPivot={selectedPivot} onPivotClicked={onPivotClicked}>
            {(props: IWithPivotsChildProps) => (
                <Dropdown
                    disabled={disableDropdown}
                    items={dropdownItems}
                    onSelect={(e, branchItem) => onSelect(branchItem.data as GitRef)}
                    {...props}
                />
            )}
        </WithPivots>
    );
}

interface IBranchPivotData {
    disableDropdown: boolean;
    selectedPivot: string;
    dropdownItems: IObservableArray<IListBoxItem<GitRef>>;
    pivots: IDropdownPivot<GitRef>[];
    onPivotClicked: (p: IDropdownPivot) => void;
}

/**
 * A lot of code is needed to set up the data for this dropdown, and there's a fair amount of state
 * to keep track of. This hook hides away the logic that sets up that state so that the component
 * can just consume the data it needs.
 */
function useBranchPivotData(
    repository: GitRepository | undefined,
    excludeBranches: boolean,
    excludeTags: boolean
): IBranchPivotData {
    const pageContext = usePageContext();
    const [disableDropdown, setDisableDropdown] = useState(true);
    const [selectedPivot, setSelectedPivot] = useState(myBranchesPivotId);

    const [myBranchItems, setMyBranchItems] = useObservableArray<IListBoxItem<GitRef>>([]);
    const [allBranchItems, setAllBranchItems] = useObservableArray<IListBoxItem<GitRef>>([]);
    const [tagItems, setTagItems] = useObservableArray<IListBoxItem<GitRef>>([]);
    const [dropdownItems, setDropdownItems] = useObservableArray<IListBoxItem<GitRef>>([]);

    const pivots = useMemo(
        () => {
            const pivots = [];

            if (!excludeBranches) {
                pivots.push({
                    id: myBranchesPivotId,
                    name: Resources.RefPickerMyBranchesTab,
                    filterPlaceholderText: Resources.RefPickerMyBranchesFilterPlaceholder,
                    showFilterBox: true,
                    items: myBranchItems
                });

                pivots.push({
                    id: allBranchesPivotId,
                    name: Resources.RefPickerAllBranchesTab,
                    filterPlaceholderText: Resources.RefPickerAllBranchesFilterPlaceholder,
                    showFilterBox: true,
                    items: allBranchItems
                });
            }

            if (!excludeTags) {
                pivots.push({
                    id: tagsPivotId,
                    name: Resources.RefPickerTagsTab,
                    filterPlaceholderText: Resources.RefPickerTagsFilterPlaceholder,
                    showFilterBox: true,
                    items: tagItems
                });
            }

            return pivots;
        },
        [myBranchItems, allBranchItems, tagItems]
    );

    // When the repository changes, reload the branch options
    useEffect(() => {
        if (!repository) {
            return;
        }

        (async () => {
            setDisableDropdown(true); // Disable the dropdown while loading is in progress

            const { allBranches, myBranches, tags } = await fetchBranchesData(pageContext, repository, excludeBranches, excludeTags);

            const newAllBranchItems = allBranches.map(branchRef => ({
                id: `branch-[${branchRef.name}]`,
                text: formatBranchName(branchRef.name),
                data: branchRef
            }));

            const newMyBranchItems = myBranches.map(branchRef => ({
                id: `my-branch-[${branchRef.name}]`,
                text: formatBranchName(branchRef.name),
                data: branchRef
            }));

            const newTagItems = tags.map(tagRef => ({
                id: `tag-[${tagRef.name}]`,
                text: formatTagName(tagRef.name),
                data: tagRef
            }));

            setMyBranchItems(newMyBranchItems);
            setAllBranchItems(newAllBranchItems);
            setTagItems(newTagItems);
            setDropdownItems([...newAllBranchItems, ...newMyBranchItems, ...newTagItems]);

            setDisableDropdown(false);
        })();
    }, [repository, excludeBranches, excludeTags]);

    const onPivotClicked = useCallback((p: IDropdownPivot) => setSelectedPivot(p.id), []);

    return { disableDropdown, selectedPivot, dropdownItems, pivots, onPivotClicked };
}

async function fetchBranchesData(
    pageContext: IVssPageContext,
    repository: GitRepository,
    excludeBranches: boolean,
    excludeTags: boolean
): Promise<{ allBranches: GitRef[]; myBranches: GitRef[]; tags: GitRef[] }> {
    const gitClient = getGitClient(pageContext);

    const myBranchesPromise = excludeBranches
        ? Promise.resolve([] as GitRef[])
        : gitClient
              .getRefs(repository.id, repository.project.id, undefined, undefined, undefined, /* includeMyBranches */ true)
              .then(myRefs => myRefs.filter(ref => ref.name.startsWith(branchRefPrefix)))
              .catch(() => [] as GitRef[]);

    const allBranchesPromise = excludeBranches
        ? Promise.resolve([] as GitRef[])
        : gitClient.getRefs(repository.id, repository.project.id, branchesFilter).catch(() => [] as GitRef[]);

    const tagsPromise = excludeTags
        ? Promise.resolve([] as GitRef[])
        : gitClient.getRefs(repository.id, repository.project.id, tagsFilter).catch(() => [] as GitRef[]);

    const [myBranches, allBranches, tags] = await Promise.all([myBranchesPromise, allBranchesPromise, tagsPromise]);

    return { allBranches, myBranches, tags };
}
